import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle, Loader2, DatabaseBackup, ShieldAlert } from "lucide-react";

export default function AdminReset() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState([]);
  const [password, setPassword] = useState("");
  const [step, setStep] = useState(0); // 0 pick, 1 reauth+confirm, 2 final confirm
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [force, setForce] = useState(false);

  const load = () => api.get("/admin/reset/preview").then((r) => setPreview(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (isSuper) load(); }, [isSuper]);

  if (!isSuper) return <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-800" data-testid="reset-forbidden">Only the Super Admin can access the Data Reset Center.</div>;
  if (!preview) return <div className="text-slate-400">Loading…</div>;

  const exposure = preview.financial_exposure;
  const atRisk = exposure.open_orders_with_payments > 0 || exposure.pending_deposits > 0 || exposure.non_zero_wallets > 0;
  const isFull = selected.length === preview.categories.length;
  const financialScopes = ["customers", "orders", "deposits", "wallet_records"];
  const touchesFinancial = selected.some((s) => financialScopes.includes(s));

  const toggle = (scope) => setSelected((prev) => prev.includes(scope) ? prev.filter((x) => x !== scope) : [...prev, scope]);
  const selectAll = () => setSelected(selected.length === preview.categories.length ? [] : preview.categories.map((c) => c.scope));

  const execute = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset/execute", {
        scopes: isFull ? ["full"] : selected, password, confirm: true, force_financial: force,
      });
      setReport(data); toast.success("Reset complete");
      setStep(0); setSelected([]); setPassword(""); setForce(false); load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.financial_exposure) {
        toast.error("Financial records present — review and tick 'force' to proceed."); setStep(1);
      } else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  const totalSelected = preview.categories.filter((c) => selected.includes(c.scope)).reduce((a, c) => a + c.total, 0);

  return (
    <div className="space-y-6 max-w-2xl" data-testid="reset-center-page">
      <div className="flex items-center gap-2">
        <DatabaseBackup className="w-6 h-6 text-blue-600" />
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Data Reset Center</h1>
      </div>
      <p className="text-sm text-slate-500">Super-admin-only. A verified backup is created before every reset. Your account, products, settings and audit logs are always preserved.</p>

      {atRisk && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 flex gap-2" data-testid="reset-exposure">
          <ShieldAlert className="w-5 h-5 shrink-0" />
          <div>
            <div className="font-semibold">Financial records present — review before resetting:</div>
            <ul className="mt-1 list-disc pl-5">
              <li>Open orders: {exposure.open_orders} (with payments: {exposure.open_orders_with_payments})</li>
              <li>Pending deposits: {exposure.pending_deposits}</li>
              <li>Non-zero wallets: {exposure.non_zero_wallets}</li>
            </ul>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-semibold text-slate-900">Select reset scope</h2>
          <button onClick={selectAll} data-testid="reset-select-all" className="text-sm font-medium text-blue-600 hover:underline">
            {isFull ? "Clear all" : "Select all (full reset)"}
          </button>
        </div>
        {preview.categories.map((c) => (
          <label key={c.scope} className="flex items-center justify-between p-3 rounded-xl border border-slate-200 cursor-pointer hover:bg-slate-50">
            <span className="text-sm font-medium text-slate-700">{c.label}</span>
            <span className="flex items-center gap-3">
              <span className="text-xs font-mono text-slate-500">{c.total} records</span>
              <input type="checkbox" checked={selected.includes(c.scope)} onChange={() => toggle(c.scope)} data-testid={`reset-scope-${c.scope}`} className="w-5 h-5" />
            </span>
          </label>
        ))}
        <button disabled={selected.length === 0} onClick={() => setStep(1)} data-testid="reset-continue-button"
          className="w-full mt-2 px-5 py-2.5 rounded-xl bg-slate-900 text-white font-semibold disabled:opacity-50">
          Continue ({totalSelected} records in {selected.length} categor{selected.length === 1 ? "y" : "ies"})
        </button>
      </div>

      {report && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-sm text-emerald-900" data-testid="reset-report">
          <div className="font-semibold mb-1">Reset complete</div>
          <div className="text-xs text-emerald-700">Backup: {report.backup_reference}</div>
          <ul className="mt-2 list-disc pl-5">
            {Object.entries(report.deleted).map(([k, v]) => <li key={k}>{k}: {v} deleted</li>)}
          </ul>
        </div>
      )}

      {step >= 1 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={() => !busy && setStep(0)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="reset-confirm-modal">
            <div className="flex items-center gap-2 text-rose-600"><AlertTriangle className="w-5 h-5" /><h3 className="font-heading font-bold text-lg">Confirm {isFull ? "FULL" : ""} data reset</h3></div>
            <div className="text-sm text-slate-600">This permanently deletes {totalSelected} records across: {preview.categories.filter((c) => selected.includes(c.scope)).map((c) => c.label).join(", ")}. A backup is taken first.</div>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Re-enter your Super Admin password"
              data-testid="reset-password" className="w-full px-3 py-2 rounded-xl border border-slate-300 outline-none text-sm" />
            {touchesFinancial && atRisk && (
              <label className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3">
                <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="reset-force-financial" className="w-4 h-4 mt-0.5" />
                I understand financial records (open orders / deposits / wallets) will be deleted and I want to proceed.
              </label>
            )}
            {step === 1 ? (
              <button onClick={() => setStep(2)} disabled={!password || (touchesFinancial && atRisk && !force)} data-testid="reset-first-confirm-button"
                className="w-full px-4 py-2.5 rounded-xl bg-rose-600 text-white font-semibold disabled:opacity-50">
                {isFull ? "Full reset — confirm once more" : "Confirm reset"}
              </button>
            ) : (
              <button onClick={execute} disabled={busy} data-testid="reset-final-confirm-button"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-rose-700 text-white font-semibold disabled:opacity-60">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Yes, permanently delete now
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
